import createContext from 'create-react-context';
import stringify from 'fast-json-stable-stringify';
import { visit, GraphQLError, print } from 'graphql';
import gql from 'graphql-tag';
import React, { Component, useContext, useState, useCallback, useEffect } from 'react';
import { filter, map, merge, pipe, share, tap, make, mergeMap, takeUntil, makeSubject, onEnd, onStart, take, toPromise, subscribe } from 'wonka';

var collectTypes = function collectTypes(obj, types) {
  if (types === void 0) types = [];

  if (Array.isArray(obj)) {
    obj.forEach(function (inner) {
      return collectTypes(inner, types);
    });
  } else if (typeof obj === 'object' && obj !== null) {
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) {
        var val = obj[key];

        if (key === '__typename' && typeof val === 'string') {
          types.push(val);
        } else if (typeof val === 'object' && val !== null) {
          collectTypes(val, types);
        }
      }
    }
  }

  return types;
};

var collectTypesFromResponse = function collectTypesFromResponse(response) {
  return collectTypes(response).filter(function (v, i, a) {
    return a.indexOf(v) === i;
  });
};

var formatNode = function formatNode(n) {
  return n.selectionSet !== undefined && n.selectionSet.selections !== undefined ? Object.assign({}, n, {
    selectionSet: Object.assign({}, n.selectionSet, {
      selections: n.selectionSet.selections.concat([{
        kind: 'Field',
        name: {
          kind: 'Name',
          value: '__typename'
        }
      }])
    })
  }) : false;
};

var formatDocument = function formatDocument(astNode) {
  return visit(astNode, {
    Field: formatNode,
    InlineFragment: formatNode,
    OperationDefinition: formatNode
  });
};

var cacheExchange = function cacheExchange(ref) {
  var forward = ref.forward;
  var client = ref.client;
  var resultCache = new Map();
  var operationCache = Object.create(null); // Adds unique typenames to query (for invalidating cache entries)

  var mapTypeNames = function mapTypeNames(operation) {
    return Object.assign({}, operation, {
      query: formatDocument(operation.query)
    });
  };

  var handleAfterMutation = afterMutation(resultCache, operationCache, client);
  var handleAfterQuery = afterQuery(resultCache, operationCache);

  var isOperationCached = function isOperationCached(operation) {
    var key = operation.key;
    var operationName = operation.operationName;
    var requestPolicy = operation.context.requestPolicy;
    return operationName === 'query' && requestPolicy !== 'network-only' && (requestPolicy === 'cache-only' || resultCache.has(key));
  };

  var shouldSkip = function shouldSkip(operation) {
    return operation.operationName !== 'mutation' && operation.operationName !== 'query';
  };

  return function (ops$) {
    var sharedOps$ = share(ops$);
    var cachedOps$ = pipe(sharedOps$, filter(function (op) {
      return !shouldSkip(op) && isOperationCached(op);
    }), map(function (operation) {
      var key = operation.key;
      var requestPolicy = operation.context.requestPolicy;
      var cachedResult = resultCache.get(key);

      if (requestPolicy === 'cache-and-network') {
        reexecuteOperation(client, operation);
      }

      if (cachedResult !== undefined) {
        return cachedResult;
      }

      return {
        operation: operation,
        data: undefined,
        error: undefined
      };
    }));
    var forwardedOps$ = pipe(merge([pipe(sharedOps$, filter(function (op) {
      return !shouldSkip(op) && !isOperationCached(op);
    }), map(mapTypeNames)), pipe(sharedOps$, filter(function (op) {
      return shouldSkip(op);
    }))]), forward, tap(function (response) {
      if (response.operation && response.operation.operationName === 'mutation') {
        handleAfterMutation(response);
      } else if (response.operation && response.operation.operationName === 'query') {
        handleAfterQuery(response);
      }
    }));
    return merge([cachedOps$, forwardedOps$]);
  };
}; // Reexecutes a given operation with the default requestPolicy


var reexecuteOperation = function reexecuteOperation(client, operation) {
  return client.reexecuteOperation(Object.assign({}, operation, {
    context: Object.assign({}, operation.context, {
      requestPolicy: 'network-only'
    })
  }));
}; // Invalidates the cache given a mutation's response


var afterMutation = function afterMutation(resultCache, operationCache, client) {
  return function (response) {
    var pendingOperations = new Set();
    collectTypesFromResponse(response.data).forEach(function (typeName) {
      var operations = operationCache[typeName] || (operationCache[typeName] = new Set());
      operations.forEach(function (key) {
        return pendingOperations.add(key);
      });
      operations.clear();
    });
    pendingOperations.forEach(function (key) {
      if (resultCache.has(key)) {
        var operation = resultCache.get(key).operation;
        resultCache.delete(key);
        reexecuteOperation(client, operation);
      }
    });
  };
}; // Mark typenames on typenameInvalidate for early invalidation


var afterQuery = function afterQuery(resultCache, operationCache) {
  return function (response) {
    var key = response.operation.key;
    var data = response.data;

    if (data === undefined) {
      return;
    }

    resultCache.set(key, response);
    collectTypesFromResponse(response.data).forEach(function (typeName) {
      var operations = operationCache[typeName] || (operationCache[typeName] = new Set());
      operations.add(key);
    });
  };
};

var generateErrorMessage = function generateErrorMessage(networkErr, graphQlErrs) {
  var error = '';

  if (networkErr !== undefined) {
    error = "[Network] " + networkErr.message;
    return error;
  }

  if (graphQlErrs !== undefined) {
    graphQlErrs.forEach(function (err) {
      error += "[GraphQL] " + err.message + "\n";
    });
  }

  return error.trim();
};

var rehydrateGraphQlError = function rehydrateGraphQlError(error) {
  if (typeof error === 'string') {
    return new GraphQLError(error);
  } else if (typeof error === 'object' && error.message) {
    return new GraphQLError(error.message, error.nodes, error.source, error.positions, error.path, error.originalError, error.extensions || {});
  } else {
    return error;
  }
};
/** An error which can consist of GraphQL errors and Network errors. */


var CombinedError =
/*@__PURE__*/
function (Error) {
  function CombinedError(ref) {
    var networkError = ref.networkError;
    var graphQLErrors = ref.graphQLErrors;
    var response = ref.response;
    var normalisedGraphQLErrors = (graphQLErrors || []).map(rehydrateGraphQlError);
    var message = generateErrorMessage(networkError, normalisedGraphQLErrors);
    Error.call(this, message);
    this.name = 'CombinedError';
    this.message = message;
    this.graphQLErrors = normalisedGraphQLErrors;
    this.networkError = networkError;
    this.response = response;
  }

  if (Error) CombinedError.__proto__ = Error;
  CombinedError.prototype = Object.create(Error && Error.prototype);
  CombinedError.prototype.constructor = CombinedError;

  CombinedError.prototype.toString = function toString() {
    return this.message;
  };

  return CombinedError;
}(Error);

var isSubscriptionOperation = function isSubscriptionOperation(operation) {
  return operation.operationName === 'subscription';
};

var subscriptionExchange = function subscriptionExchange(ref) {
  var forwardSubscription = ref.forwardSubscription;
  return function (ref) {
    var forward = ref.forward;

    var createSubscriptionSource = function createSubscriptionSource(operation) {
      // This excludes the query's name as a field although subscription-transport-ws does accept it since it's optional
      var observableish = forwardSubscription({
        key: operation.key.toString(36),
        query: print(operation.query),
        variables: operation.variables,
        context: Object.assign({}, operation.context)
      });
      return make(function (ref) {
        var _next = ref[0];
        var complete = ref[1]; // TODO: The conversion of the result here is very similar to fetch;
        // We can maybe extract the logic into generic GraphQL utilities

        var sub = observableish.subscribe({
          next: function next(result) {
            return _next({
              operation: operation,
              data: result.data || undefined,
              error: Array.isArray(result.errors) ? new CombinedError({
                graphQLErrors: result.errors,
                response: undefined
              }) : undefined
            });
          },
          error: function error(err) {
            return _next({
              operation: operation,
              data: undefined,
              error: new CombinedError({
                networkError: err,
                response: undefined
              })
            });
          },
          complete: complete
        }); // NOTE: Destructuring sub is avoided here to preserve its potential binding

        return function () {
          return sub.unsubscribe();
        };
      });
    };

    return function (ops$) {
      var sharedOps$ = share(ops$);
      var subscriptionResults$ = pipe(sharedOps$, filter(isSubscriptionOperation), mergeMap(function (operation) {
        var key = operation.key;
        var teardown$ = pipe(sharedOps$, filter(function (op) {
          return op.operationName === 'teardown' && op.key === key;
        }));
        return pipe(createSubscriptionSource(operation), takeUntil(teardown$));
      }));
      var forward$ = pipe(sharedOps$, filter(function (op) {
        return !isSubscriptionOperation(op);
      }), forward);
      return merge([subscriptionResults$, forward$]);
    };
  };
}; // tslint:disable no-console


var debugExchange = function debugExchange(ref) {
  var forward = ref.forward;
  return function (ops$) {
    return pipe(ops$, tap(function (op) {
      return console.log('[Exchange debug]: Incoming operation: ', op);
    }), forward, tap(function (result) {
      return console.log('[Exchange debug]: Completed operation: ', result);
    }));
  };
};
/** A default exchange for debouncing GraphQL requests. */


var dedupExchange = function dedupExchange(ref) {
  var forward = ref.forward;
  var inFlight = new Set();
  return function (ops$) {
    return pipe(forward(pipe(ops$, filter(function (ref) {
      var operationName = ref.operationName;
      var key = ref.key;

      if (operationName !== 'query') {
        return true;
      }

      var hasInFlightOp = inFlight.has(key);

      if (!hasInFlightOp) {
        inFlight.add(key);
      }

      return !hasInFlightOp;
    }))), tap(function (res) {
      inFlight.delete(res.operation.key);
    }));
  };
};
/** A default exchange for fetching GraphQL requests. */


var fetchExchange = function fetchExchange(ref) {
  var forward = ref.forward;

  var isOperationFetchable = function isOperationFetchable(operation) {
    var operationName = operation.operationName;
    return operationName === 'query' || operationName === 'mutation';
  };

  return function (ops$) {
    var sharedOps$ = share(ops$);
    var fetchResults$ = pipe(sharedOps$, filter(isOperationFetchable), mergeMap(function (operation) {
      var key = operation.key;
      var teardown$ = pipe(sharedOps$, filter(function (op) {
        return op.operationName === 'teardown' && op.key === key;
      }));
      return pipe(createFetchSource(operation), takeUntil(teardown$));
    }));
    var forward$ = pipe(sharedOps$, filter(function (op) {
      return !isOperationFetchable(op);
    }), forward);
    return merge([fetchResults$, forward$]);
  };
};

var createFetchSource = function createFetchSource(operation) {
  if (operation.operationName === 'subscription') {
    throw new Error('Received a subscription operation in the httpExchange. You are probably trying to create a subscription. Have you added a subscriptionExchange?');
  }

  return make(function (ref) {
    var next = ref[0];
    var complete = ref[1];
    var abortController = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    var context = operation.context;
    var extraOptions = typeof context.fetchOptions === 'function' ? context.fetchOptions() : context.fetchOptions || {};
    var fetchOptions = Object.assign({}, {
      body: JSON.stringify({
        query: print(operation.query),
        variables: operation.variables
      }),
      method: 'POST'
    }, extraOptions, {
      headers: Object.assign({}, {
        'content-type': 'application/json'
      }, extraOptions.headers),
      signal: abortController !== undefined ? abortController.signal : undefined
    });
    executeFetch(operation, fetchOptions).then(function (result) {
      if (result !== undefined) {
        next(result);
      }

      complete();
    });
    return function () {
      if (abortController !== undefined) {
        abortController.abort();
      }
    };
  });
};

var executeFetch = function executeFetch(operation, opts) {
  var response;
  var ref = operation.context;
  var url = ref.url;
  return fetch(url, opts).then(function (res) {
    response = res;
    checkStatus(opts.redirect, response);
    return response.json();
  }).then(function (result) {
    return {
      operation: operation,
      data: result.data,
      error: Array.isArray(result.errors) ? new CombinedError({
        graphQLErrors: result.errors,
        response: response
      }) : undefined
    };
  }).catch(function (err) {
    if (err.name === 'AbortError') {
      return undefined;
    }

    return {
      operation: operation,
      data: undefined,
      error: new CombinedError({
        networkError: err,
        response: response
      })
    };
  });
};

var checkStatus = function checkStatus(redirectMode, response) {
  if (redirectMode === void 0) redirectMode = 'follow';
  var statusRangeEnd = redirectMode === 'manual' ? 400 : 300;

  if (response.status < 200 || response.status > statusRangeEnd) {
    throw new Error(response.statusText);
  }
};
/** This is always the last exchange in the chain; No operation should ever reach it */


var fallbackExchangeIO = function fallbackExchangeIO(ops$) {
  return pipe(ops$, tap(function (ref) {
    var operationName = ref.operationName;

    if (operationName !== 'teardown' && process.env.NODE_ENV !== 'production') {
      console.warn("No exchange has handled operations of type \"" + operationName + "\". Check whether you've added an exchange responsible for these operations.");
    }
  }),
  /* All operations that skipped through the entire exchange chain should be filtered from the output */
  filter(function () {
    return false;
  }));
};
/** This composes an array of Exchanges into a single ExchangeIO function */


var composeExchanges = function composeExchanges(exchanges) {
  if (exchanges.length === 1) {
    return exchanges[0];
  }

  return function (ref) {
    var client = ref.client;
    var outerForward = ref.forward;
    return exchanges.reduceRight(function (forward, exchange) {
      return exchange({
        client: client,
        forward: forward
      });
    }, outerForward);
  };
};

var defaultExchanges = [dedupExchange, cacheExchange, fetchExchange];

var createClient = function createClient(opts) {
  return new Client(opts);
};
/** The URQL application-wide client library. Each execute method starts a GraphQL request and returns a stream of results. */


var Client = function Client(opts) {
  var this$1 = this;
  this.activeOperations = Object.create(null);

  this.createOperationContext = function (opts) {
    var ref = opts || {};
    var requestPolicy = ref.requestPolicy;
    if (requestPolicy === void 0) requestPolicy = 'cache-first';
    return Object.assign({}, {
      url: this$1.url,
      fetchOptions: this$1.fetchOptions
    }, opts, {
      requestPolicy: requestPolicy
    });
  };

  this.createRequestOperation = function (type, ref, opts) {
    var key = ref.key;
    var query = ref.query;
    var variables = ref.variables;
    return {
      key: key,
      query: query,
      variables: variables,
      operationName: type,
      context: this$1.createOperationContext(opts)
    };
  };

  this.reexecuteOperation = function (operation) {
    // Reexecute operation only if any subscribers are still subscribed to the
    // operation's exchange results
    if ((this$1.activeOperations[operation.key] || 0) > 0) {
      this$1.dispatchOperation(operation);
    }
  };

  this.executeQuery = function (query, opts) {
    var operation = this$1.createRequestOperation('query', query, opts);
    return this$1.executeRequestOperation(operation);
  };

  this.executeSubscription = function (query, opts) {
    var operation = this$1.createRequestOperation('subscription', query, opts);
    return this$1.executeRequestOperation(operation);
  };

  this.executeMutation = function (query, opts) {
    var operation = this$1.createRequestOperation('mutation', query, opts);
    return this$1.executeRequestOperation(operation);
  };

  this.url = opts.url;
  this.fetchOptions = opts.fetchOptions; // This subject forms the input of operations; executeOperation may be
  // called to dispatch a new operation on the subject

  var ref = makeSubject();
  var operations$ = ref[0];
  var nextOperation = ref[1];
  this.operations$ = operations$;
  this.dispatchOperation = nextOperation;
  var exchanges = opts.exchanges !== undefined ? opts.exchanges : defaultExchanges; // All exchange are composed into a single one and are called using the constructed client
  // and the fallback exchange stream

  this.exchange = composeExchanges(exchanges); // All operations run through the exchanges in a pipeline-like fashion
  // and this observable then combines all their results

  this.results$ = share(this.exchange({
    client: this,
    forward: fallbackExchangeIO
  })(this.operations$));
};
/** Counts up the active operation key and dispatches the operation */


Client.prototype.onOperationStart = function onOperationStart(operation) {
  var key = operation.key;
  this.activeOperations[key] = (this.activeOperations[key] || 0) + 1;
  this.dispatchOperation(operation);
};
/** Deletes an active operation's result observable and sends a teardown signal through the exchange pipeline */


Client.prototype.onOperationEnd = function onOperationEnd(operation) {
  var key = operation.key;
  var prevActive = this.activeOperations[key] || 0;
  var newActive = this.activeOperations[key] = prevActive <= 0 ? 0 : prevActive - 1;

  if (newActive <= 0) {
    this.dispatchOperation(Object.assign({}, operation, {
      operationName: 'teardown'
    }));
  }
};
/** Executes an Operation by sending it through the exchange pipeline It returns an observable that emits all related exchange results and keeps track of this observable's subscribers. A teardown signal will be emitted when no subscribers are listening anymore. */


Client.prototype.executeRequestOperation = function executeRequestOperation(operation) {
  var this$1 = this;
  var key = operation.key;
  var operationName = operation.operationName;
  var operationResults$ = pipe(this.results$, filter(function (res) {
    return res.operation.key === key;
  }));

  if (operationName === 'mutation') {
    // A mutation is always limited to just a single result and is never shared
    return pipe(operationResults$, onStart(function () {
      return this$1.dispatchOperation(operation);
    }), take(1));
  }

  return pipe(operationResults$, onStart(function () {
    return this$1.onOperationStart(operation);
  }), onEnd(function () {
    return this$1.onOperationEnd(operation);
  }));
}; // but not to error catastrophically if someone is just playing around


var defaultOptions = {
  url: '/graphql'
};
var Context = createContext(createClient(defaultOptions));
var Provider = Context.Provider;
var Consumer = Context.Consumer;
/* tslint:disable */

var hash = function hash(x) {
  /* prettier-ignore */
  for (var h = 5381 | 0, i = 0, l = x.length | 0; i < l; i++) {
    h = (h << 5) + h + x.charCodeAt(i);
  }

  return h >>> 0;
};
/* tslint:enable */


var docNameCache = Object.create(null);

var getKeyForQuery = function getKeyForQuery(doc) {
  if (doc.__key !== undefined) {
    return doc.__key;
  } // Using print() can be expensive, so we just check the definition nodes' names


  var name = doc.definitions.reduce(function (acc, definition) {
    var node = definition;
    return acc + (node.name !== undefined ? node.name.value : '');
  }, ''); // This is for inputs that are not using constant references. In such a case
  // we use the Document names, but we can't use those if they're not unique

  if (process.env.NODE_ENV !== 'production' && name !== '') {
    var printed = print(doc);

    if (!(name in docNameCache)) {
      docNameCache[name] = printed;
    } else if (docNameCache[name] !== printed) {
      console.warn('Warning: Encountered multiple DocumentNodes with the same name.');
    }
  } // If no name is present the stringified document is used


  if (name === '') {
    name = print(doc);
  }

  var key = hash(name);
  doc.__key = key;
  return key;
};

var getKeyForRequest = function getKeyForRequest(query, vars) {
  var docKey = getKeyForQuery(query);

  if (vars === undefined || vars === null) {
    return docKey;
  }

  return hash('' + docKey + stringify(vars));
};

var createRequest = function createRequest(q, vars) {
  var query = typeof q === 'string' ? gql([q]) : q;
  return {
    key: getKeyForRequest(query, vars),
    query: query,
    variables: vars || {}
  };
};

var noop = function noop() {
  /* noop */
};

var MutationHandler =
/*@__PURE__*/
function (Component$$1) {
  function MutationHandler() {
    var this$1 = this;
    Component$$1.apply(this, arguments);
    this.state = {
      fetching: false,
      data: undefined,
      error: undefined
    };

    this.executeMutation = function (variables) {
      this$1.setState({
        fetching: true,
        error: undefined,
        data: undefined
      });
      var request = createRequest(this$1.props.query, variables);
      return pipe(this$1.props.client.executeMutation(request), toPromise).then(function (result) {
        var data = result.data;
        var error = result.error;
        this$1.setState({
          fetching: false,
          data: data,
          error: error
        });
        return result;
      }).catch(function (networkError) {
        var error = new CombinedError({
          networkError: networkError
        });
        this$1.setState({
          fetching: false,
          data: undefined,
          error: error
        });
        return {
          data: undefined,
          error: error
        };
      });
    };
  }

  if (Component$$1) MutationHandler.__proto__ = Component$$1;
  MutationHandler.prototype = Object.create(Component$$1 && Component$$1.prototype);
  MutationHandler.prototype.constructor = MutationHandler;

  MutationHandler.prototype.render = function render() {
    return this.props.children(Object.assign({}, this.state, {
      executeMutation: this.executeMutation
    }));
  };

  return MutationHandler;
}(Component);

var Mutation = function Mutation(props) {
  return React.createElement(Consumer, null, function (client) {
    return React.createElement(MutationHandler, Object.assign({}, props, {
      client: client
    }));
  });
};

var QueryHandler =
/*@__PURE__*/
function (Component$$1) {
  function QueryHandler() {
    var this$1 = this;
    Component$$1.apply(this, arguments);
    this.unsubscribe = noop;
    this.request = createRequest(this.props.query, this.props.variables);

    this.executeQuery = function (opts) {
      this$1.unsubscribe();
      this$1.setState({
        fetching: true
      });
      var ref = pipe(this$1.props.client.executeQuery(this$1.request, Object.assign({}, {
        requestPolicy: this$1.props.requestPolicy
      }, opts)), subscribe(function (ref) {
        var data = ref.data;
        var error = ref.error;
        this$1.setState({
          fetching: false,
          data: data,
          error: error
        });
      }));
      var teardown = ref[0];
      this$1.unsubscribe = teardown;
    };

    this.state = {
      executeQuery: this.executeQuery,
      data: undefined,
      error: undefined,
      fetching: false
    };
  }

  if (Component$$1) QueryHandler.__proto__ = Component$$1;
  QueryHandler.prototype = Object.create(Component$$1 && Component$$1.prototype);
  QueryHandler.prototype.constructor = QueryHandler;

  QueryHandler.prototype.componentDidMount = function componentDidMount() {
    this.executeQuery();
  };

  QueryHandler.prototype.componentDidUpdate = function componentDidUpdate() {
    var newRequest = createRequest(this.props.query, this.props.variables);

    if (newRequest.key !== this.request.key) {
      this.request = newRequest;
      this.executeQuery();
    }
  };

  QueryHandler.prototype.componentWillUnmount = function componentWillUnmount() {
    this.unsubscribe();
  };

  QueryHandler.prototype.render = function render() {
    return this.props.children(this.state);
  };

  return QueryHandler;
}(Component);

var Query = function Query(props) {
  return React.createElement(Consumer, null, function (client) {
    return React.createElement(QueryHandler, Object.assign({}, props, {
      client: client
    }));
  });
};

var SubscriptionHandler =
/*@__PURE__*/
function (Component$$1) {
  function SubscriptionHandler() {
    var this$1 = this;
    Component$$1.apply(this, arguments);
    this.unsubscribe = noop;
    this.request = createRequest(this.props.query, this.props.variables);

    this.executeSubscription = function () {
      this$1.setState({
        fetching: true
      });
      var ref = pipe(this$1.props.client.executeSubscription(this$1.request), subscribe(function (ref) {
        var data = ref.data;
        var error = ref.error;
        var ref$1 = this$1.props;
        var handler = ref$1.handler;
        this$1.setState({
          fetching: false,
          data: handler !== undefined ? handler(this$1.state.data, data) : data,
          error: error
        });
      }));
      var teardown = ref[0];
      this$1.unsubscribe = teardown;
    };

    this.state = {
      data: undefined,
      error: undefined,
      fetching: false
    };
  }

  if (Component$$1) SubscriptionHandler.__proto__ = Component$$1;
  SubscriptionHandler.prototype = Object.create(Component$$1 && Component$$1.prototype);
  SubscriptionHandler.prototype.constructor = SubscriptionHandler;

  SubscriptionHandler.prototype.componentDidMount = function componentDidMount() {
    this.executeSubscription();
  };

  SubscriptionHandler.prototype.componentDidUpdate = function componentDidUpdate() {
    var newRequest = createRequest(this.props.query, this.props.variables);

    if (newRequest.key !== this.request.key) {
      this.request = newRequest;
      this.executeSubscription();
    }
  };

  SubscriptionHandler.prototype.componentWillUnmount = function componentWillUnmount() {
    this.unsubscribe();
  };

  SubscriptionHandler.prototype.render = function render() {
    return this.props.children(this.state);
  };

  return SubscriptionHandler;
}(Component);

var Subscription = function Subscription(props) {
  return React.createElement(Consumer, null, function (client) {
    return React.createElement(SubscriptionHandler, Object.assign({}, props, {
      client: client
    }));
  });
};

var useMutation = function useMutation(query) {
  var client = useContext(Context);
  var ref = useState({
    fetching: false,
    error: undefined,
    data: undefined
  });
  var state = ref[0];
  var setState = ref[1];

  var executeMutation = function executeMutation(variables) {
    setState({
      fetching: true,
      error: undefined,
      data: undefined
    });
    var request = createRequest(query, variables);
    return pipe(client.executeMutation(request), toPromise).then(function (result) {
      var data = result.data;
      var error = result.error;
      setState({
        fetching: false,
        data: data,
        error: error
      });
      return result;
    });
  };

  return [state, executeMutation];
};

var useQuery = function useQuery(args) {
  var unsubscribe = noop;
  var client = useContext(Context);
  var request = createRequest(args.query, args.variables);
  var ref = useState({
    fetching: false,
    error: undefined,
    data: undefined
  });
  var state = ref[0];
  var setState = ref[1];
  var executeQuery = useCallback(function (opts) {
    unsubscribe();
    setState(function (s) {
      return Object.assign({}, s, {
        fetching: true
      });
    });
    var ref = pipe(client.executeQuery(request, Object.assign({}, {
      requestPolicy: args.requestPolicy
    }, opts)), subscribe(function (ref) {
      var data = ref.data;
      var error = ref.error;
      return setState({
        fetching: false,
        data: data,
        error: error
      });
    }));
    var teardown = ref[0];
    unsubscribe = teardown;
  }, [request.key]);
  useEffect(function () {
    executeQuery();
    return unsubscribe;
  }, [request.key]);
  return [state, executeQuery];
};

var useSubscription = function useSubscription(args, handler) {
  var unsubscribe = noop;
  var client = useContext(Context);
  var request = createRequest(args.query, args.variables);
  var ref = useState({
    error: undefined,
    data: undefined
  });
  var state = ref[0];
  var setState = ref[1];
  var executeSubscription = useCallback(function () {
    unsubscribe();
    var ref = pipe(client.executeSubscription(request), subscribe(function (ref) {
      var data = ref.data;
      var error = ref.error;
      setState(function (s) {
        return {
          data: handler !== undefined ? handler(s.data, data) : data,
          error: error
        };
      });
    }));
    var teardown = ref[0];
    unsubscribe = teardown;
  }, [request.key]);
  useEffect(function () {
    executeSubscription();
    return unsubscribe;
  }, [request.key]);
  return [state];
};

export { createClient, Client, Context, Provider, Consumer, Mutation, Query, Subscription, defaultExchanges, cacheExchange, subscriptionExchange, debugExchange, dedupExchange, fetchExchange, fallbackExchangeIO, composeExchanges, useMutation, useQuery, useSubscription, noop, CombinedError, getKeyForRequest, createRequest, formatDocument, collectTypesFromResponse };